// src/hooks/useEvaluationPeriod.js
//
// Reusable React hook for evaluation-period state
// ===============================================
//
// WHY THIS HOOK EXISTS
// --------------------
// The utility layer (evaluationPeriodUtils.js) knows HOW to calculate periods,
// but it does not store UI state.
//
// This hook is the stateful bridge between:
//   - pure date-period logic
//   - React pages/components
//
// It manages:
//   - selected mode (MONTH / YEAR / WEEK / DAY / MANUAL)
//   - current cursor date for non-manual navigation
//   - manual from/to values
//
// It returns a clean API that report pages can use directly.
//
// DESIGN GOAL
// -----------
// A page should be able to do:
//
//   const period = useEvaluationPeriod({
//     defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
//     initialDate: new Date(),
//     allowedModes: [
//       EVALUATION_PERIOD_TYPES.MONTH,
//       EVALUATION_PERIOD_TYPES.YEAR,
//       EVALUATION_PERIOD_TYPES.WEEK,
//       EVALUATION_PERIOD_TYPES.DAY,
//       EVALUATION_PERIOD_TYPES.MANUAL,
//     ],
//   });
//
// and then consume:
//
//   period.mode
//   period.from
//   period.to
//   period.displayLabel
//   period.goPrev()
//   period.goNext()
//   period.setMode(...)
//   period.manualFrom / manualTo
//
// This makes the pattern very easy to reuse across pages later.
//
// NEW CAPABILITY: allowedModes
// ----------------------------
// This hook now supports:
//
//   allowedModes: [ ... ]
//
// That allows a consuming page to centrally restrict which evaluation period
// types are available.
//
// Example:
// - Income Report:
//     all modes allowed
//
// - Commission Report for role "user":
//     only MONTH allowed
//
// IMPORTANT
// ---------
// The hook remains generic:
// - it does NOT know anything about roles
// - it does NOT know anything about reports
// - it only enforces the list of allowed modes provided by the page
//

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EVALUATION_PERIOD_OPTIONS,
  EVALUATION_PERIOD_TYPES,
  buildDefaultManualRange,
  buildEvaluationPeriodRange,
  normalizeDate,
  normalizeEvaluationPeriodMode,
  parseDateInputValue,
  shiftEvaluationCursor,
} from "../utils/evaluationPeriodUtils";

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Build a normalized, de-duplicated list of allowed modes.
 *
 * Why this helper exists:
 * - callers may pass raw strings with different casing
 * - callers may pass duplicate values
 * - callers may pass an empty list accidentally
 *
 * Behavior:
 * - If allowedModes is empty / invalid -> fall back to ALL available modes
 * - Output order follows EVALUATION_PERIOD_OPTIONS order
 *   so the dropdown stays stable and predictable
 */
const buildAllowedModes = (allowedModes) => {
  const allModesInUiOrder = EVALUATION_PERIOD_OPTIONS.map((option) => option.value);

  if (!Array.isArray(allowedModes) || allowedModes.length === 0) {
    return allModesInUiOrder;
  }

  const normalizedSet = new Set(
    allowedModes
      .map((mode) => normalizeEvaluationPeriodMode(mode))
      .filter(Boolean)
  );

  const filtered = allModesInUiOrder.filter((mode) => normalizedSet.has(mode));

  return filtered.length > 0 ? filtered : allModesInUiOrder;
};

/**
 * Pick a safe default mode that is guaranteed to exist in the allowed list.
 *
 * Why:
 * - a page may request defaultMode=MONTH
 * - but allowedModes may contain only YEAR
 *
 * In that case the hook must not keep an invalid initial state.
 */
const resolveSafeDefaultMode = (requestedDefaultMode, allowedModes) => {
  const normalizedDefault = normalizeEvaluationPeriodMode(
    requestedDefaultMode || EVALUATION_PERIOD_TYPES.MONTH
  );

  if (allowedModes.includes(normalizedDefault)) {
    return normalizedDefault;
  }

  return allowedModes[0] || EVALUATION_PERIOD_TYPES.MONTH;
};

/* -------------------------------------------------------------------------- */
/* Main hook                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * useEvaluationPeriod
 *
 * @param {Object} options
 * @param {string} [options.defaultMode="MONTH"]
 * @param {Date|string} [options.initialDate=new Date()]
 * @param {string[]} [options.allowedModes]
 *
 * @returns {Object}
 * Hook API with:
 * - current mode
 * - calculated from/to
 * - display label
 * - navigation helpers
 * - manual input values
 * - filtered mode options
 */
const useEvaluationPeriod = (options = {}) => {
  /**
   * Normalize the allowed mode list once per render.
   *
   * This becomes the source of truth for:
   * - what the picker shows
   * - what setMode() is allowed to accept
   * - whether the current mode remains valid after props change
   */
  const allowedModes = useMemo(() => {
    return buildAllowedModes(options.allowedModes);
  }, [options.allowedModes]);

  /**
   * Resolve a safe default mode that is guaranteed to exist in allowedModes.
   */
  const safeDefaultMode = useMemo(() => {
    return resolveSafeDefaultMode(options.defaultMode, allowedModes);
  }, [options.defaultMode, allowedModes]);

  /**
   * Normalize initial cursor anchor date.
   *
   * This is used as:
   * - navigation anchor for non-manual modes
   * - default manual range seed
   */
  const initialDate = useMemo(() => {
    return normalizeDate(options.initialDate || new Date());
  }, [options.initialDate]);

  /**
   * Initial manual range is derived from the initial date.
   *
   * This gives a sensible starting value when a page opens in MANUAL mode
   * or when the user switches there for the first time.
   */
  const initialManualRange = useMemo(() => {
    return buildDefaultManualRange(initialDate);
  }, [initialDate]);

  /**
   * Core states managed by the hook.
   *
   * mode:
   *   selected evaluation-period type
   *
   * cursorDate:
   *   anchor date used for MONTH / YEAR / WEEK / DAY navigation
   *
   * manualFrom / manualTo:
   *   explicit date picker values used only in MANUAL mode
   */
  const [mode, setModeState] = useState(safeDefaultMode);
  const [cursorDate, setCursorDate] = useState(initialDate);
  const [manualFrom, setManualFrom] = useState(initialManualRange.from);
  const [manualTo, setManualTo] = useState(initialManualRange.to);

  /**
   * Keep current mode valid if allowedModes changes later.
   *
   * This is important for dynamic pages where allowed modes may depend on:
   * - role
   * - permissions
   * - feature toggles
   *
   * Example:
   * - page first renders with all modes
   * - later role context resolves and only MONTH is allowed
   *
   * In that case we automatically move to the first allowed mode.
   */
  useEffect(() => {
    if (!allowedModes.includes(mode)) {
      setModeState(safeDefaultMode);
    }
  }, [allowedModes, mode, safeDefaultMode]);

  /**
   * Derived canonical period object.
   *
   * This is the single source of truth for:
   * - from
   * - to
   * - label
   * - whether manual inputs should be visible
   */
  const period = useMemo(() => {
    return buildEvaluationPeriodRange({
      mode,
      cursorDate,
      manualFrom,
      manualTo,
    });
  }, [mode, cursorDate, manualFrom, manualTo]);

  /**
   * UI options filtered according to allowedModes.
   *
   * This is what consuming components should render in the mode dropdown.
   */
  const filteredOptions = useMemo(() => {
    return EVALUATION_PERIOD_OPTIONS.filter((option) =>
      allowedModes.includes(option.value)
    );
  }, [allowedModes]);

  /**
   * Change period mode.
   *
   * UX rule:
   * - If switching INTO MANUAL:
   *     prefill manual inputs with the current computed range
   *   so the user starts from something sensible instead of empty fields.
   *
   * - If switching OUT OF MANUAL:
   *     use manualFrom as the new cursor anchor when possible
   *   so navigation continues near the date the user was just working with.
   *
   * SAFETY RULE:
   * - If nextMode is not allowed, ignore it.
   */
  const setMode = useCallback(
    (nextMode) => {
      const normalized = normalizeEvaluationPeriodMode(nextMode);

      if (!allowedModes.includes(normalized)) {
        return;
      }

      if (normalized === mode) return;

      if (normalized === EVALUATION_PERIOD_TYPES.MANUAL) {
        setManualFrom(period.from);
        setManualTo(period.to);
      }

      if (mode === EVALUATION_PERIOD_TYPES.MANUAL) {
        const manualAnchor = parseDateInputValue(manualFrom, cursorDate);
        if (manualAnchor) {
          setCursorDate(manualAnchor);
        }
      }

      setModeState(normalized);
    },
    [allowedModes, mode, period.from, period.to, manualFrom, cursorDate]
  );

  /**
   * Go one period backward.
   *
   * Disabled logically in MANUAL mode because MANUAL uses explicit dates
   * rather than relative cursor-based navigation.
   */
  const goPrev = useCallback(() => {
    if (mode === EVALUATION_PERIOD_TYPES.MANUAL) return;

    setCursorDate((prev) =>
      shiftEvaluationCursor({
        mode,
        cursorDate: prev,
        direction: -1,
      })
    );
  }, [mode]);

  /**
   * Go one period forward.
   *
   * Same navigation rule as goPrev().
   */
  const goNext = useCallback(() => {
    if (mode === EVALUATION_PERIOD_TYPES.MANUAL) return;

    setCursorDate((prev) =>
      shiftEvaluationCursor({
        mode,
        cursorDate: prev,
        direction: +1,
      })
    );
  }, [mode]);

  /**
   * Manual setters are exposed directly so consuming pages/components
   * can bind them to <input type="date" /> safely.
   */
  const setManualFromValue = useCallback((value) => {
    setManualFrom(value || "");
  }, []);

  const setManualToValue = useCallback((value) => {
    setManualTo(value || "");
  }, []);

  return {
    // Static / filtered options
    options: filteredOptions,
    allowedModes,

    // State
    mode,
    cursorDate,
    manualFrom,
    manualTo,

    // Derived
    from: period.from,
    to: period.to,
    fromDate: period.fromDate,
    toDate: period.toDate,
    displayLabel: period.displayLabel,
    isManual: period.isManual,
    showManualInputs: period.showManualInputs,

    // Actions
    setMode,
    goPrev,
    goNext,
    setManualFrom: setManualFromValue,
    setManualTo: setManualToValue,
    setCursorDate,
  };
};

export default useEvaluationPeriod;